'use client';

import { X, Upload, CheckCircle, AlertCircle, Trash2, ChevronUp } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface UploadTask {
    id: string;
    name: string;
    type: 'upload' | 'delete';
    status: 'pending' | 'success' | 'error';
    progress?: number;
    error?: string;
    completedAt?: number;
    startedAt: number;
}

export function UploadProgress() {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
    const [totalSuccessful, setTotalSuccessful] = useState(0);
    const tasksRef = useRef<Map<string, UploadTask>>(new Map());
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Listen for custom upload and delete events
    useEffect(() => {
        const handleUploadStart = (event: CustomEvent) => {
            const { id, name } = event.detail;

            setUploadTasks(prev => {
                // Check if task already exists to prevent duplicates
                const exists = prev.find(task => task.id === id);
                if (exists) return prev;

                const newTask: UploadTask = {
                    id,
                    name,
                    type: 'upload',
                    status: 'pending',
                    progress: 0,
                    startedAt: Date.now()
                };

                tasksRef.current.set(id, newTask);
                return [...prev, newTask];
            });
            setIsVisible(true);
        };

        const handleDeleteStart = (event: CustomEvent) => {
            const { id, name } = event.detail;

            setUploadTasks(prev => {
                // Check if task already exists to prevent duplicates
                const exists = prev.find(task => task.id === id);
                if (exists) return prev;

                const newTask: UploadTask = {
                    id,
                    name,
                    type: 'delete',
                    status: 'pending',
                    progress: 0,
                    startedAt: Date.now()
                };

                tasksRef.current.set(id, newTask);
                return [...prev, newTask];
            });
            setIsVisible(true);
        };

        const handleUploadProgress = (event: CustomEvent) => {
            const { id, progress } = event.detail;
            setUploadTasks(prev => prev.map(task => {
                if (task.id === id) {
                    const updatedTask = { ...task, progress };
                    tasksRef.current.set(id, updatedTask);
                    return updatedTask;
                }
                return task;
            }));
        };

        const handleUploadSuccess = (event: CustomEvent) => {
            const { id } = event.detail;
            setUploadTasks(prev => prev.map(task => {
                if (task.id === id) {
                    const updatedTask: UploadTask = {
                        ...task,
                        status: 'success' as const,
                        progress: 100,
                        completedAt: Date.now()
                    };
                    tasksRef.current.set(id, updatedTask);
                    return updatedTask;
                }
                return task;
            }));
            // Increment the persistent successful counter
            setTotalSuccessful(prev => prev + 1);
        };

        const handleUploadError = (event: CustomEvent) => {
            const { id, error } = event.detail;
            setUploadTasks(prev => prev.map(task => {
                if (task.id === id) {
                    const updatedTask: UploadTask = {
                        ...task,
                        status: 'error' as const,
                        error,
                        completedAt: Date.now()
                    };
                    tasksRef.current.set(id, updatedTask);
                    return updatedTask;
                }
                return task;
            }));
        };

        const handleUploadComplete = (event: CustomEvent) => {
            const { id } = event.detail;
            // Remove completed tasks after 8 seconds
            setTimeout(() => {
                setUploadTasks(prev => {
                    const filtered = prev.filter(task => task.id !== id);
                    tasksRef.current.delete(id);
                    return filtered;
                });
            }, 8000);
        };

        // Add event listeners
        window.addEventListener('upload:start', handleUploadStart as EventListener);
        window.addEventListener('upload:progress', handleUploadProgress as EventListener);
        window.addEventListener('upload:success', handleUploadSuccess as EventListener);
        window.addEventListener('upload:error', handleUploadError as EventListener);
        window.addEventListener('upload:complete', handleUploadComplete as EventListener);

        window.addEventListener('delete:start', handleDeleteStart as EventListener);
        window.addEventListener('delete:progress', handleUploadProgress as EventListener);
        window.addEventListener('delete:success', handleUploadSuccess as EventListener);
        window.addEventListener('delete:error', handleUploadError as EventListener);
        window.addEventListener('delete:complete', handleUploadComplete as EventListener);

        return () => {
            window.removeEventListener('upload:start', handleUploadStart as EventListener);
            window.removeEventListener('upload:progress', handleUploadProgress as EventListener);
            window.removeEventListener('upload:success', handleUploadSuccess as EventListener);
            window.removeEventListener('upload:error', handleUploadError as EventListener);
            window.removeEventListener('upload:complete', handleUploadComplete as EventListener);

            window.removeEventListener('delete:start', handleDeleteStart as EventListener);
            window.removeEventListener('delete:progress', handleUploadProgress as EventListener);
            window.removeEventListener('delete:success', handleUploadSuccess as EventListener);
            window.removeEventListener('delete:error', handleUploadError as EventListener);
            window.removeEventListener('delete:complete', handleUploadComplete as EventListener);

            // Clean up timeout
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []);

    // Auto-hide when all tasks are completed
    useEffect(() => {
        const hasActivePending = uploadTasks.some(task => task.status === 'pending');
        const hasAnyTasks = uploadTasks.length > 0;

        // Clear any existing timeout
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        // If all tasks are completed (no pending) and component is visible, auto-hide after 4 seconds
        if (hasAnyTasks && !hasActivePending && isVisible && !isAnimatingOut) {
            hideTimeoutRef.current = setTimeout(() => {
                handleAnimatedHide();
            }, 4000);
        }
    }, [uploadTasks, isVisible, isAnimatingOut]);

    // Don't auto-hide - let user manually dismiss
    // useEffect(() => {
    //     const hasActiveUploads = uploadTasks.some(task => task.status === 'pending');
    //     const allCompleted = uploadTasks.every(task => task.status !== 'pending');

    //     if (!hasActiveUploads && allCompleted && uploadTasks.length > 0 && isVisible) {
    //         setTimeout(() => setIsVisible(false), 5000);
    //     }
    // }, [uploadTasks, isVisible]);

    const handleAnimatedHide = useCallback(() => {
        // Clear any pending timeout
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        setIsAnimatingOut(true);

        // After animation completes, actually hide the component
        setTimeout(() => {
            setIsVisible(false);
            setIsAnimatingOut(false);
        }, 300); // Match animation duration
    }, []);

    const clearAll = useCallback(() => {
        setUploadTasks([]);
        setTotalSuccessful(0);
        tasksRef.current.clear();
        handleAnimatedHide();
    }, [handleAnimatedHide]);

    const handleHide = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleAnimatedHide();
    }, [handleAnimatedHide]);

    if (!isVisible) return null;

    const activeUploads = uploadTasks.filter(task => task.status === 'pending');
    const completedUploads = uploadTasks.filter(task => task.status === 'success');
    const failedUploads = uploadTasks.filter(task => task.status === 'error');

    const totalProgress = activeUploads.length > 0
        ? activeUploads.reduce((sum, task) => sum + (task.progress || 0), 0) / activeUploads.length
        : 0;

    return (
        <div className="fixed bottom-4 right-4 z-50">
            <div className={`bg-background border rounded-lg shadow-lg p-4 w-80 max-h-96 overflow-hidden transition-all duration-300 ease-in-out ${isAnimatingOut
                ? 'transform translate-y-full opacity-0'
                : 'transform translate-y-0 opacity-100'
                }`}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        {activeUploads.some(task => task.type === 'delete') ? (
                            <Trash2 className="h-4 w-4 text-red-500" />
                        ) : (
                            <Upload className="h-4 w-4 text-blue-500" />
                        )}
                        <h3 className="text-sm font-semibold">
                            {activeUploads.some(task => task.type === 'delete') ? 'Eliminando Archivos' : 'Subida de Archivos'}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeUploads.length > 0 && (
                            <div className="flex items-center gap-1">
                                <div className={`w-2 h-2 rounded-full animate-pulse ${activeUploads.some(task => task.type === 'delete') ? 'bg-red-500' : 'bg-blue-500'
                                    }`}></div>
                                <span className="text-xs text-muted-foreground">
                                    {activeUploads.filter(task => task.type === 'upload').length > 0 &&
                                        `${activeUploads.filter(task => task.type === 'upload').length} subiendo`}
                                    {activeUploads.filter(task => task.type === 'delete').length > 0 &&
                                        `${activeUploads.filter(task => task.type === 'delete').length} eliminando`}
                                </span>
                            </div>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="h-6 w-6 p-0"
                            aria-label={isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
                        >
                            <ChevronUp className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </Button>
                    </div>
                </div>

                {/* Overall Progress Bar */}
                {activeUploads.length > 0 && (
                    <div className="mb-3">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>Progreso General</span>
                            <span>{Math.round(totalProgress)}%</span>
                        </div>
                        <Progress value={totalProgress} className="h-2" />
                    </div>
                )}

                {/* Upload List - Expandable Details */}
                {isExpanded && (
                    <div className="space-y-2 max-h-48 overflow-y-auto mt-2">
                        {uploadTasks.map((task) => (
                            <div key={task.id} className="rounded p-2 border flex flex-col gap-1 bg-muted/30">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-1">
                                        {task.type === 'delete' ? (
                                            <Trash2 className="h-3 w-3 text-red-500" />
                                        ) : (
                                            <Upload className="h-3 w-3 text-blue-500" />
                                        )}
                                        <span className="text-xs font-medium truncate max-w-[120px]" title={task.name}>{task.name}</span>
                                    </div>
                                    <span className="text-xs">
                                        {task.status === 'pending' && `${task.progress || 0}%`}
                                        {task.status === 'success' && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                                        {task.status === 'error' && <AlertCircle className="inline h-4 w-4 text-red-500 ml-1" />}
                                    </span>
                                </div>
                                <Progress value={task.progress || (task.status === 'success' ? 100 : 0)} className="h-1" />
                                {task.status === 'error' && (
                                    <div className="text-xs text-red-500 mt-1">{task.error}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Completed/Failed summary (optional) */}
                {(completedUploads.length > 0 || failedUploads.length > 0) && !isExpanded && (
                    <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
                        {completedUploads.length > 0 && <div><CheckCircle className="inline h-3 w-3 text-green-500 mr-1" />{completedUploads.length} completados</div>}
                        {failedUploads.length > 0 && <div><AlertCircle className="inline h-3 w-3 text-red-500 mr-1" />{failedUploads.length} fallidos</div>}
                    </div>
                )}

                {/* Clear all and dismiss buttons */}
                <div className="flex justify-between mt-2">
                    <Button variant="ghost" size="sm" onClick={handleHide} className="text-xs">
                        <X className="h-3 w-3 mr-1" />
                        Ocultar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs">Limpiar Todo</Button>
                </div>
            </div>
        </div>
    );
} 